<script setup>
import { ref, computed, onMounted } from "vue";
import { useI18n } from "vue-i18n";
import Section from "@/widgets/profile-drawer/ui/internal/Section.vue";
import ReadonlyField from "@/widgets/profile-drawer/ui/fields/ReadonlyField.vue";
import EditableField from "@/widgets/profile-drawer/ui/fields/EditableField.vue";
import { Switch } from "@/shared/ui/switch";
import { Checkbox } from "@/shared/ui/checkbox";
import { Spinner } from "@/shared/ui/spinner";
import { getRoleList } from "@/entities/role/api";
import { getOrganizationList } from "@/entities/organization/api";
import { getUserMeOrganizations } from "@/entities/user/api";

const props = defineProps({
  data: { type: Object, required: true },
  organizations: { type: Array, default: () => [] },
  editMode: { type: Boolean, default: false },
  canEdit: { type: Boolean, default: false },
  self: { type: Boolean, default: false },
});

const emit = defineEmits(["update"]);

const { locale } = useI18n();
const isKz = computed(() => locale.value === "kk");

const rolesAll = ref([]);
const organizationsAll = ref([]);
const loadingRefs = ref(false);

function setField(key, value) {
  emit("update", key, value);
}

const organizationOptions = computed(() =>
  organizationsAll.value.map((o) => ({
    value: o.id,
    label: o.name || o.title || `#${o.id}`,
  }))
);

const organizationName = computed(() => {
  const id = props.data.organization_id;
  if (id == null) return null;
  const found =
    organizationsAll.value.find((o) => o.id === id) ||
    props.organizations.find((o) => o.id === id);
  return found?.name || found?.title || `#${id}`;
});

const selectedRoleCodes = computed(() =>
  Array.isArray(props.data.role_codes) ? props.data.role_codes : []
);

const roleNamesByCode = computed(() => {
  const map = {};
  for (const r of rolesAll.value) map[r.code] = roleLabel(r);
  if (Array.isArray(props.data.roles)) {
    for (const r of props.data.roles) {
      if (r?.code && !map[r.code]) map[r.code] = roleLabel(r);
    }
  }
  return map;
});

function toggleRole(code, checked) {
  const next = new Set(selectedRoleCodes.value);
  if (checked) next.add(code);
  else next.delete(code);
  setField("role_codes", Array.from(next));
}

function roleLabel(role) {
  if (!role) return "";
  if (isKz.value && role.label_kz) return role.label_kz;
  return role.label || role.name || role.code || "";
}

function rolesFromMeOrganizations(res) {
  const orgs = Array.isArray(res?.payload) ? res.payload : [];
  const map = new Map();
  for (const item of orgs) {
    const roles = Array.isArray(item?.roles) ? item.roles : [];
    for (const r of roles) {
      if (r?.code && !map.has(r.code)) map.set(r.code, r);
    }
  }
  return Array.from(map.values());
}

onMounted(async () => {
  loadingRefs.value = true;
  try {
    if (props.self) {
      rolesAll.value = rolesFromMeOrganizations(await getUserMeOrganizations());
      return;
    }
    const [rolesRes, orgsRes] = await Promise.all([
      getRoleList({}),
      getOrganizationList({}),
    ]);
    if (rolesRes?.success && rolesRes?.payload?.items?.length) {
      rolesAll.value = rolesRes.payload.items;
    }
    if (orgsRes?.success && orgsRes?.payload?.length) {
      organizationsAll.value = orgsRes.payload;
    }
  } catch (e) {
    console.error(e);
  } finally {
    loadingRefs.value = false;
  }
});
</script>

<template>
  <div class="flex flex-col">
    <Section
      plain
      :title="$t('Доступ')"
      :sub="
        canEdit
          ? $t('Активность и организация')
          : $t('Только администратор может изменить')
      "
    >
      <div class="grid grid-cols-2 gap-x-5 gap-y-3.5 max-sm:grid-cols-1">
        <div class="flex flex-col gap-1.5">
          <div class="text-[12.5px] text-muted-foreground">
            {{ $t("Активный") }}
          </div>
          <div class="flex items-center gap-2.5 px-2.5 py-1.5 min-h-[34px]">
            <Switch
              :model-value="!!data.is_active"
              :disabled="!editMode || !canEdit"
              @update:model-value="setField('is_active', $event)"
            />
            <div
              class="text-sm font-medium"
              :class="
                data.is_active ? 'text-green-600' : 'text-muted-foreground'
              "
            >
              {{ data.is_active ? $t("Включён") : $t("Отключён") }}
            </div>
          </div>
        </div>

        <EditableField
          v-if="editMode && canEdit"
          :label="$t('Организация')"
          :model-value="data.organization_id"
          :options="organizationOptions"
          :placeholder="$t('Выберите организацию')"
          @update:model-value="setField('organization_id', $event)"
        />
        <ReadonlyField
          v-else
          :label="$t('Организация')"
          :value="organizationName"
          :is-without-field="true"
        />
      </div>
    </Section>

    <Section
      plain
      :title="$t('Роли')"
      :sub="
        canEdit
          ? $t('Список ролей пользователя')
          : $t('Только администратор может изменить')
      "
    >
      <div
        v-if="loadingRefs"
        class="flex items-center gap-2 text-sm text-muted-foreground"
      >
        <Spinner size="sm" class="text-current" />
        {{ $t("Загрузка ролей…") }}
      </div>

      <template v-else-if="editMode && canEdit">
        <div v-if="!rolesAll.length" class="text-sm text-muted-foreground">
          {{ $t("Список ролей пуст.") }}
        </div>
        <div v-else class="grid grid-cols-2 gap-x-5 gap-y-2 max-sm:grid-cols-1">
          <label
            v-for="r in rolesAll"
            :key="r.code"
            class="flex items-start gap-2.5 px-2 py-1 cursor-pointer rounded-md hover:bg-muted"
          >
            <Checkbox
              :model-value="selectedRoleCodes.includes(r.code)"
              @change="toggleRole(r.code, $event)"
            />
            <div class="flex flex-col leading-tight">
              <div class="text-sm font-medium">{{ roleLabel(r) }}</div>
              <div class="text-[11.5px] text-muted-foreground">
                {{ r.code }}
              </div>
            </div>
          </label>
        </div>
      </template>

      <template v-else>
        <div
          v-if="!selectedRoleCodes.length"
          class="text-sm text-muted-foreground"
        >
          {{ $t("Не указано") }}
        </div>
        <div v-else class="flex flex-wrap gap-2">
          <div
            v-for="code in selectedRoleCodes"
            :key="code"
            class="inline-flex items-center px-2.5 py-1 rounded-md border border-input bg-background text-[13px]"
          >
            {{ roleNamesByCode[code] || code }}
          </div>
        </div>
      </template>
    </Section>
  </div>
</template>
